import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetTimeSlotsConfigurationByAppIdResponse,
  QueryTimeSlotsConfigurationsResponse,
  TimeSlotsConfiguration,
  TimeSlotsConfigurationQuery,
  TimeSlotsConfigurationsQueryBuilder,
  UpdateTimeSlotsConfiguration,
  getTimeSlotsConfiguration as universalGetTimeSlotsConfiguration,
  getTimeSlotsConfigurationByAppId as universalGetTimeSlotsConfigurationByAppId,
  queryTimeSlotsConfigurations as universalQueryTimeSlotsConfigurations,
  typedQueryTimeSlotsConfigurations as universalTypedQueryTimeSlotsConfigurations,
  updateTimeSlotsConfiguration as universalUpdateTimeSlotsConfiguration,
} from './bookings-timeslots-configuration-v1-time-slots-configuration-time-slots-configuration.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

/** @internal */
export function getTimeSlotsConfiguration(
  httpClient: HttpClient
): GetTimeSlotsConfigurationSignature {
  return (timeSlotsConfigurationId: string) =>
    universalGetTimeSlotsConfiguration(
      timeSlotsConfigurationId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTimeSlotsConfigurationSignature {
  /**
   * Retrieves a time slots configuration by ID.
   * @param - Time slots configuration ID.
   * @returns Retrieved time slots configuration.
   */
  (timeSlotsConfigurationId: string): Promise<
    NonNullablePaths<
      TimeSlotsConfiguration,
      `displayTimeZone.basedOn` | `staffSelection.strategy` | `appId`,
      3
    >
  >;
}

/** @internal */
export function getTimeSlotsConfigurationByAppId(
  httpClient: HttpClient
): GetTimeSlotsConfigurationByAppIdSignature {
  return (appId: string) =>
    universalGetTimeSlotsConfigurationByAppId(
      appId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTimeSlotsConfigurationByAppIdSignature {
  /**
   * Retrieves a time slots configuration by app ID.
   * @param - App ID.
   */
  (appId: string): Promise<
    NonNullablePaths<
      GetTimeSlotsConfigurationByAppIdResponse,
      | `timeSlotsConfiguration.displayTimeZone.basedOn`
      | `timeSlotsConfiguration.staffSelection.strategy`
      | `timeSlotsConfiguration.appId`,
      4
    >
  >;
}

/** @internal */
export function updateTimeSlotsConfiguration(
  httpClient: HttpClient
): UpdateTimeSlotsConfigurationSignature {
  return (
    _id: string,
    timeSlotsConfiguration: NonNullablePaths<
      UpdateTimeSlotsConfiguration,
      `revision`,
      2
    >
  ) =>
    universalUpdateTimeSlotsConfiguration(
      _id,
      timeSlotsConfiguration,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTimeSlotsConfigurationSignature {
  /**
   * Updates a time slots configuration.
   *
   * Each time the time slots configuration is updated, `revision` increments by 1.
   * The current `revision` must be passed when updating the time slots configuration.
   * This ensures you're working with the latest time slots configuration and prevents unintended overwrites.
   * @param - Time slots configuration ID.
   * @returns Updated time slots configuration.
   */
  (
    _id: string,
    timeSlotsConfiguration: NonNullablePaths<
      UpdateTimeSlotsConfiguration,
      `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      TimeSlotsConfiguration,
      `displayTimeZone.basedOn` | `staffSelection.strategy` | `appId`,
      3
    >
  >;
}

/** @internal */
export function queryTimeSlotsConfigurations(
  httpClient: HttpClient
): QueryTimeSlotsConfigurationsSignature {
  return () =>
    universalQueryTimeSlotsConfigurations(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryTimeSlotsConfigurationsSignature {
  /**
   * Retrieves a list of time slots configurations, given the provided paging, filtering, and sorting.
   */
  (): TimeSlotsConfigurationsQueryBuilder;
}

export function typedQueryTimeSlotsConfigurations(
  httpClient: HttpClient
): TypedQueryTimeSlotsConfigurationsSignature {
  return (query: TimeSlotsConfigurationQuery) =>
    universalTypedQueryTimeSlotsConfigurations(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryTimeSlotsConfigurationsSignature {
  /** */
  (query: TimeSlotsConfigurationQuery): Promise<
    NonNullablePaths<
      QueryTimeSlotsConfigurationsResponse,
      | `timeSlotsConfigurations`
      | `timeSlotsConfigurations.${number}.displayTimeZone.basedOn`
      | `timeSlotsConfigurations.${number}.staffSelection.strategy`
      | `timeSlotsConfigurations.${number}.appId`,
      5
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  BasedOn,
  CreateTimeSlotsConfigurationRequest,
  CreateTimeSlotsConfigurationResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DisplayTimeZone,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetTimeSlotsConfigurationByAppIdRequest,
  GetTimeSlotsConfigurationByAppIdResponse,
  GetTimeSlotsConfigurationRequest,
  GetTimeSlotsConfigurationResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  QueryTimeSlotsConfigurationsRequest,
  QueryTimeSlotsConfigurationsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  SplitInterval,
  StaffSelection,
  Strategy,
  TimeSlotsConfiguration,
  TimeSlotsConfigurationQuerySpec,
  TimeSlotsConfigurationsQueryBuilder,
  TimeSlotsConfigurationsQueryResult,
  UpdateTimeSlotsConfiguration,
  UpdateTimeSlotsConfigurationRequest,
  UpdateTimeSlotsConfigurationResponse,
  WebhookIdentityType,
  utils,
} from './bookings-timeslots-configuration-v1-time-slots-configuration-time-slots-configuration.universal.js';
